import { Component, lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react"
import { X } from "lucide-react"

const NativeEmojiPicker = lazy(() => import("./NativeEmojiPicker"))

// A failed optional chunk must not unmount the meeting or its media connection.
class EmojiLoadBoundary extends Component<{ children: ReactNode; onRetry: () => void }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() { return { failed: true } }
  render() {
    if (!this.state.failed) return this.props.children
    return <div role="alert" className="p-3 text-sm text-white/80">
      <p>Couldn't load emoji.</p>
      <button type="button" onClick={this.props.onRetry} className="mt-2 rounded-md bg-white/10 px-3 py-2 font-medium hover:bg-white/20">Try again</button>
    </div>
  }
}

type CallEmojiPickerProps = {
  onSelect: (emoji: string) => void
  onClose: () => void
  skinTone?: string
  height?: number | string
}

/** Shared compact picker. Focus starts here while the optional emoji chunk loads. */
export function CallEmojiPicker({ onSelect, onClose, skinTone, height }: CallEmojiPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [load, setLoad] = useState({ attempt: 0, Picker: NativeEmojiPicker })
  const Picker = load.Picker
  useEffect(() => {
    // A cached lazy import may already have focused search; do not move focus back out.
    if (!containerRef.current?.contains(document.activeElement)) containerRef.current?.focus()
  }, [])
  return (
    <div ref={containerRef} tabIndex={-1} className="min-w-0 rounded-xl outline-none">
      <div className="flex items-center justify-between px-3 pt-2">
        <span className="text-xs font-semibold text-white/80">All emoji</span>
        <button type="button" aria-label="Close emoji picker" onClick={onClose} className="rounded-md p-1.5 text-white/70 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#f6a35c]">
          <X className="h-4 w-4" />
        </button>
      </div>
      <EmojiLoadBoundary key={load.attempt} onRetry={() => setLoad((current) => ({ attempt: current.attempt + 1, Picker: lazy(() => import("./NativeEmojiPicker")) }))}>
        <Suspense fallback={<p role="status" className="p-4 text-sm text-white/70">Loading emoji…</p>}>
          <Picker onSelect={onSelect} skinTone={skinTone} height={height} />
        </Suspense>
      </EmojiLoadBoundary>
    </div>
  )
}
